import { stegaClean } from 'next-sanity';
import { Slot } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/utils';
import type {
  AspectRatio,
  DesktopAspectRatio,
  MobileAspectRatio,
} from '../sanity/studio/constants';

interface Props {
  children: ReactNode;
  aspectRatioMobile: MobileAspectRatio | null;
  aspectRatioDesktop: DesktopAspectRatio | null;
  className?: string;
}

export function AspectRatioMobileDesktop({
  children,
  aspectRatioMobile = '9:16',
  aspectRatioDesktop = '16:9',
  className,
  asChild = false,
}: Props & { asChild?: boolean }) {
  const mobileClasses = aspectRatioMobile
    ? getAspectRatioStringMobile(stegaClean(aspectRatioMobile))
    : '';
  const desktopClasses = aspectRatioDesktop
    ? getAspectRatioStringDesktop(stegaClean(aspectRatioDesktop))
    : '';

  const Comp = asChild ? Slot.Root : 'div';

  return (
    <Comp
      className={cn('h-0 w-full', mobileClasses, desktopClasses, className)}
    >
      {children}
    </Comp>
  );
}

export function AspectRatioSingle({
  children,
  aspectRatio,
  className,
}: {
  children: ReactNode;
  aspectRatio: AspectRatio;
  className?: string;
}) {
  const classes = aspectRatio
    ? getAspectRatioString(stegaClean(aspectRatio))
    : '';

  return <div className={cn('h-0 w-full', classes, className)}>{children}</div>;
}

function getAspectRatioString(ratio: AspectRatio) {
  switch (ratio) {
    case '9:16':
      return 'aspect-w-9 aspect-h-16';
    case '3:4':
      return 'aspect-w-3 aspect-h-4';
    case '4:3':
      return 'aspect-w-4 aspect-h-3';
    case '1:1':
      return 'aspect-w-1 aspect-h-1';
    case '16:9':
      return 'aspect-w-16 aspect-h-9';
    case '21:9':
      return 'aspect-w-[21] aspect-h-9';
    default: {
      const _exhaustiveCheck: never = ratio;
      return _exhaustiveCheck;
    }
  }
}

function getAspectRatioStringMobile(
  ratio: NonNullable<Props['aspectRatioMobile']>
) {
  switch (ratio) {
    case '9:16':
      return 'aspect-w-9 aspect-h-16';
    case '3:4':
      return 'aspect-w-3 aspect-h-4';
    case '1:1':
      return 'aspect-w-1 aspect-h-1';
    case '4:3':
      return 'aspect-w-4 aspect-h-3';
    default: {
      const _exhaustiveCheck: never = ratio;
      return _exhaustiveCheck;
    }
  }
}

function getAspectRatioStringDesktop(
  ratio: NonNullable<Props['aspectRatioDesktop']>
) {
  switch (ratio) {
    case '16:9':
      return 'lg:aspect-w-16 lg:aspect-h-9';
    case '4:3':
      return 'lg:aspect-w-4 lg:aspect-h-3';
    case '21:9':
      return 'lg:aspect-w-[21] lg:aspect-h-9';
    case '3:4':
      return 'lg:aspect-w-3 lg:aspect-h-4';
    default: {
      const _exhaustiveCheck: never = ratio;
      return _exhaustiveCheck;
    }
  }
}
